import { z } from 'zod';
import { fileIdSchema } from '../files/stored-file.js';
export const verificationOutcomeSchema = z.enum(['clear', 'not-clear', 'inconclusive']);
/** Optional on retained records; absent historical facts are never manufactured. */
export const credentialDetailsSchema = z.object({
    credentialType: z.string().max(100).default(''),
    issuingState: z.string().max(100).default(''),
    verificationStatus: z.enum(['not-verified', 'verified', 'not-clear', 'inconclusive']).default('not-verified'),
    verificationSource: z.string().max(200).default(''),
    verificationNote: z.string().max(1000).default(''),
    verifiedAt: z.string().datetime().nullable().default(null),
    verifiedBy: z.string().max(200).default(''),
    originalIssuedDate: z.string().max(100).default(''),
    originalExpiryDate: z.string().max(100).default(''),
}).strict();
const httpsEvidenceLinkSchema = z.string().url().refine((url) => new URL(url).protocol === 'https:', 'Use an HTTPS evidence link.');
/**
 * An HR upload the record rests on (#188): the id of an upload HR put through quarantine for this
 * record. It is never evidence by itself — the record counts once that upload is COMMITTED, and
 * the derived evidence then carries `evidenceFileId` instead (`apps/api/src/data/evidence.ts`).
 */
export const evidenceUploadIdSchema = z.string().uuid();
export const competencyAssessmentSchema = z.object({
    jobProfileKey: z.string().min(1),
    assessmentType: z.enum(['initial', 'ongoing']),
    formId: z.string().min(1),
    evaluator: z.string().trim().min(1).max(200),
    evaluatorTitle: z.string().trim().min(1).max(200),
    /** An EXTERNAL link, for a form held in an outside system of record (ADR-009 Q4). */
    evidenceUrl: httpsEvidenceLinkSchema.optional(),
    /** The completed form, uploaded by HR through quarantine (#188). */
    evidenceUploadId: evidenceUploadIdSchema.optional(),
    /** DERIVED once the upload is committed; never accepted from a request (`direct-record.ts`). */
    evidenceFileId: fileIdSchema.optional(),
}).strict().refine((entry) => entry.evidenceUrl !== undefined || entry.evidenceUploadId !== undefined || entry.evidenceFileId !== undefined, {
    message: 'Upload the completed form or link it in its external system.',
    path: ['evidenceUploadId'],
});
export const completedReferenceCheckSchema = z.object({
    referenceId: z.string().trim().min(1).max(200),
    checkedAt: z.string().datetime(),
    outcome: verificationOutcomeSchema,
    evidenceUrl: httpsEvidenceLinkSchema,
}).strict();
export const directRecordInputSchema = z.object({
    supervisorSignatureVerified: z.boolean().optional(),
    orientationChecklistVerified: z.boolean().optional(),
    vaccineDisposition: z.enum(['accepted-offer', 'prior-evidence', 'medical-exception', 'declined', 'vaccinated']).optional(),
    decliningReason: z.string().trim().max(2000).optional(),
    freeOfferVerified: z.boolean().optional(),
    offerTimingVerified: z.boolean().optional(),
    policyVersion: z.string().trim().max(1000).optional(),
    signatureVerified: z.boolean().optional(),
    educationTopics: z.string().trim().max(2000).optional(),
    workHistoryDisposition: z.enum(['history-reviewed', 'no-prior-employment']).optional(),
    completedChecks: z.array(completedReferenceCheckSchema).max(10).optional(),
    occurredAt: z.string().datetime(),
    reference: z.string().max(200).default(''),
    expiresAt: z.string().datetime().nullable().default(null),
    performedBy: z.string().trim().max(200).default(''),
    source: z.string().max(200).default(''),
    outcome: verificationOutcomeSchema.default('clear'),
    note: z.string().max(1000).default(''),
    /**
     * An EXTERNAL link (ADR-009 Q4): kept for evidence held in an outside system of record — QuickBooks
     * Workforce, a registry lookup. Anything Vigeo holds itself is an uploaded file instead.
     */
    evidenceUrl: httpsEvidenceLinkSchema.nullable().default(null),
    /**
     * The quarantine upload the record rests on (#188). The instance id is the `recordId` the upload
     * was reserved under, so the approval's `store-file` effect commits it like any other.
     */
    evidenceUploadId: evidenceUploadIdSchema.optional(),
    /** The id the HR upload intent reserved; becomes the recorded instance's id. Request only. */
    recordId: z.string().uuid().optional(),
    /**
     * The committed file the record rests on (#183), for evidence the app holds itself — a signed
     * acknowledgment's stored PDF, or a committed HR upload. DERIVED, never accepted from a request:
     * `direct-record.ts` refuses it, and `evidence.ts` sets it only from a FileRecord that exists.
     * Either this or `evidenceUrl` retains the evidence.
     */
    evidenceFileId: fileIdSchema.optional(),
    credentialDetails: credentialDetailsSchema.optional(),
    competency: z.array(competencyAssessmentSchema).optional(),
});
export function evidenceSourceOf(record) {
    if (record.evidenceFileId !== undefined)
        return 'stored-file';
    return record.evidenceUrl ? 'external-link' : 'none';
}
export const hrRecordSubmissionSchema = directRecordInputSchema.extend({ recordedBy: z.string().min(1) });
/** Same policy-driven completion checks at the API boundary and during retained-evidence evaluation. */
export function complianceEvidenceErrors(rule, record) {
    if (rule.evidenceMode === 'standard')
        return [];
    const errors = [];
    if (!record.performedBy.trim())
        errors.push('Name the person who performed or verified the work.');
    if (!record.evidenceUrl && record.evidenceFileId === undefined)
        errors.push('Link the retained evidence.');
    if (!record.policyVersion?.trim())
        errors.push('Record the approved policy or form version.');
    if (record.outcome !== 'clear')
        errors.push('The outcome must be satisfactory before this requirement can be completed.');
    if (rule.evidenceMode === 'signed' && record.signatureVerified !== true)
        errors.push('Verify the signature on the retained document.');
    if (rule.evidenceMode === 'education' && !record.educationTopics?.trim())
        errors.push('Record the education topics and participation evidence.');
    if (rule.evidenceMode === 'history-verification' && !record.workHistoryDisposition)
        errors.push('Record history verification or the reviewed no-prior-employment pathway.');
    if (rule.evidenceMode === 'orientation') {
        if (record.signatureVerified !== true || record.supervisorSignatureVerified !== true)
            errors.push('Verify employee and supervisor signatures and actual dates on the checklist.');
        if (record.orientationChecklistVerified !== true)
            errors.push('Verify all 41 topics plus Other have applicable Yes/N/A responses.');
    }
    if (rule.evidenceMode === 'hepatitis-b') {
        if (!record.vaccineDisposition || record.vaccineDisposition === 'vaccinated')
            errors.push('Select the reviewed Hepatitis B evidence pathway.');
        if (record.vaccineDisposition === 'declined' && record.signatureVerified !== true)
            errors.push('Verify the signed Hepatitis B refusal.');
        if (record.vaccineDisposition === 'accepted-offer' || record.vaccineDisposition === 'declined') {
            if (record.freeOfferVerified !== true || record.offerTimingVerified !== true)
                errors.push('Verify the free offer after training within ten working days of initial assignment.');
        }
    }
    if (rule.evidenceMode === 'influenza') {
        if (!['vaccinated', 'declined'].includes(record.vaccineDisposition ?? ''))
            errors.push('Record vaccination or the reason for declining.');
        if (record.vaccineDisposition === 'declined' && !record.decliningReason?.trim())
            errors.push('Record the actual flu vaccination declining reason.');
    }
    if (rule.evidenceMode === 'references') {
        const checks = record.completedChecks ?? [];
        const unique = new Set(checks.map((check) => check.referenceId.trim().toLowerCase()));
        if (unique.size !== checks.length)
            errors.push('Each completed check must identify a different reference.');
        if (rule.minimumChecks === null)
            errors.push('Compliance must approve the required reference count in Agency settings.');
        else if (checks.length < rule.minimumChecks)
            errors.push(`Record at least ${rule.minimumChecks} completed reference checks.`);
        if (checks.some((check) => check.outcome !== 'clear'))
            errors.push('Resolve adverse or inconclusive reference results.');
        if (checks.some((check) => check.checkedAt > record.occurredAt))
            errors.push('Reference checks cannot be dated after the recorded completion.');
    }
    return errors;
}
//# sourceMappingURL=evidence-details.js.map